// ============================================================
// styles/useStyles.js — All inline style objects
// ============================================================

/**
 * @param {object} params
 * @param {"light"|"dark"} params.theme
 * @param {"xs"|"sm"|"md"|"lg"|"xl"|"xxl"} params.screenSize
 * @param {object} params.spacing   — { padding, margin, gap }
 * @param {object} params.fonts     — { title, subtitle, body, small, tiny }
 * @param {"en"|"ar"} params.language
 */
const useStyles = ({ theme, screenSize, spacing, fonts, language }) => {
  const isDark = theme === "dark";
  const isXs = screenSize === "xs";
  const isRtl = language === "ar";

  return {
    // ── Layout ──────────────────────────────────────────────
    container: {
      direction: isRtl ? "rtl" : "ltr",
    },

    // ── Header card ─────────────────────────────────────────
    header: {
      background: isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.9)",
      border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
      borderRadius: isXs ? "1rem" : "1.5rem",
      padding: spacing.padding,
      marginBottom: spacing.margin,
      boxShadow: isDark
        ? "0 1.25rem 1.5625rem -0.3125rem rgba(0,0,0,0.5)"
        : "0 1.25rem 1.5625rem -0.3125rem rgba(0,0,0,0.1)",
    },
    card1: {
      background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      WebkitBackdropFilter: theme === "dark" ? "blur(25px) saturate(180%)" : "blur(10px)",
      border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
      borderRadius: screenSize.isMobile ? "12px" : screenSize.isTablet ? "20px" : "28px",
      padding: screenSize.isMobile ? "16px" : screenSize.isTablet ? "20px" : "28px",
      boxShadow: theme === "dark" ? "0 12px 40px rgba(0,0,0,0.15)" : "0 12px 40px rgba(0,0,0,0.08)",
      marginBottom: screenSize.isMobile ? "16px" : screenSize.isTablet ? "20px" : "24px",
      transition: "all 0.3s ease",
      cursor: "pointer",
      direction: isRtl ? "rtl" : "ltr",
    },

    headerContent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: isXs ? "flex-start" : "center",
      flexDirection: isXs ? "column" : "row",
      gap: spacing.gap,
    },

    title: {
      color: isDark ? "white" : "#1a1a1a",
      fontSize: "28px",
      fontWeight: "700",
      margin: 0,
      marginBottom: "0.5rem",
    },

    subtitle: {
      color: isDark ? "rgba(255,255,255,0.8)" : "rgba(26,26,26,0.7)",
      fontSize: fonts.subtitle,
      margin: 0,
    },

    // ── Primary action button ────────────────────────────────
    createBtn: {
      background: "#2e7cba",
      color: "white",
      border: "none",
      padding: isXs ? "0.75rem 1rem" : "0.75rem 1.5rem",
      borderRadius: "0.75rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      transition: "all 0.3s ease",
      boxShadow: "0 0.25rem 0.75rem rgba(46,124,186,0.3)",
      whiteSpace: "nowrap",
    },

    // ── Tabs ────────────────────────────────────────────────
    tabContainer: {
      display: "flex",
      gap: isXs ? "0.5rem" : "1rem",
      marginBottom: spacing.margin,
      padding: isXs ? "0.75rem" : "1rem",
      background: isDark
        ? "rgba(255,255,255,0.03)"
        : "rgba(255,255,255,0.6)",
      borderRadius: "1rem",
      backdropFilter: "blur(10px)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.05)",
      boxShadow: isDark
        ? "0 4px 12px rgba(0,0,0,0.15)"
        : "0 2px 8px rgba(0,0,0,0.06)",
      transition: "all 0.3s ease",
    },

    tab: {
      background: "transparent",
      border: "none",
      padding: isXs ? "0.625rem 1rem" : "0.875rem 1.5rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      color: isDark
        ? "rgba(255,255,255,0.5)"
        : "rgba(26,26,26,0.6)",
      borderRadius: "0.75rem",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      overflow: "hidden",
      letterSpacing: "0.01em",
      textTransform: "capitalize",
      whiteSpace: "nowrap",
    },

    activeTab: {
      color: isDark ? "white" : "#ffffff",
      // background:          isDark
      //   ? "linear-gradient(135deg, rgba(46,124,186,0.3), rgba(59,130,246,0.2))"
      //   : "linear-gradient(135deg, rgba(255,255,255,0.8), rgba(248,250,252,0.6))",
      background: "#2e7cba",

      // boxShadow:           isDark
      //   ? "0 4px 12px rgba(46,124,186,0.2), inset 0 1px 2px rgba(255,255,255,0.1)"
      //   : "0 4px 12px rgba(46,124,186,0.1), inset 0 1px 2px rgba(255,255,255,0.5)",
      border: isDark
        ? "1px solid rgba(59,130,246,0.4)"
        : "1px solid rgba(46,124,186,0.2)",
    },

    // ── Stats grid ───────────────────────────────────────────
    statsGrid: {
      display: "grid",
      gridTemplateColumns: isXs
        ? "1fr"
        : screenSize === "sm"
          ? "repeat(2, 1fr)"
          : "repeat(4, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
    },

    statCard: {
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      padding: "1.25rem",
      borderRadius: isXs ? "1rem" : "1rem",
      boxShadow: isDark
        ? "0 0.5rem 2rem rgba(0,0,0,0.3)"
        : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
      transition: "all 0.3s ease",
    },

    // ── Filter container ─────────────────────────────────────
    filterContainer: {
      display: "grid",
      gridTemplateColumns: isXs
        ? "1fr"
        : screenSize === "sm"
          ? "repeat(2, 1fr)"
          : "repeat(3, 1fr)",
      gap: spacing.gap,
      marginBottom: spacing.margin,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
      padding: spacing.padding,
      borderRadius: "1rem",
    },

    filterInput: {
      background: isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.9)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.2)"
        : "1px solid rgba(0,0,0,0.1)",
      borderRadius: "0.5rem",
      padding: isXs ? "0.625rem 0.75rem" : "0.75rem 1rem",
      color: isDark ? "white" : "#1a1a1a",
      fontSize: fonts.body,
      outline: "none",
      width: "100%",
    },

    // ── Table ────────────────────────────────────────────────
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: "0",
      minWidth: isXs ? "600px" : "900px",
      textAlign: "center",
    },

    tableHeader: {
      background: isDark
        ? "linear-gradient(135deg, rgba(55,65,81,0.8), rgba(75,85,99,0.8))"
        : "linear-gradient(135deg, rgba(248,250,252,0.95), rgba(241,245,249,0.95))",
      color: isDark ? "#f1f5f9" : "#1e293b",
      padding: isXs ? "0.75rem 0.5rem" : "1rem",
      textAlign: "center",
      fontSize: fonts.tiny,
      fontWeight: "700",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      borderRadius: "0.2rem 0.05rem 0 0",
      borderBottom: isDark
        ? "2px solid rgba(59,130,246,0.3)"
        : "2px solid rgba(59,130,246,0.1)",
    },

    tableCell: {
      padding: isXs ? "0.75rem 0.5rem" : "1rem",
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(148,163,184,0.08)",
      color: isDark
        ? "rgba(248,250,252,0.95)"
        : "rgba(30,41,59,0.9)",
      fontSize: fonts.body,
      fontWeight: "500",
      textAlign: "center",
    },

    actionBtn: {
      background: "rgba(59,130,246,0.1)",
      color: "#2e7cba",
      border: "1px solid rgba(59,130,246,0.2)",
      padding: isXs ? "0.375rem 0.5rem" : "0.5rem 0.75rem",
      borderRadius: "0.5rem",
      cursor: "pointer",
      fontSize: fonts.small,
      fontWeight: "500",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      gap: "0.25rem",
    },

    // ── Modal overlay ────────────────────────────────────────
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.7)",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      zIndex: 2000,
      backdropFilter: "blur(10px)",
      padding: isXs ? "0" : "2rem",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
    },

    // ── Modal content box ────────────────────────────────────
    modalContent: {
      background: isDark
        ? "rgba(30,41,59,0.6)"
        : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(25px)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(0,0,0,0.1)",
      borderRadius: isXs ? "1rem" : "1.25rem",
      padding: isXs ? "1.5rem" : "2rem",
      width: isXs ? "100%" : "90%",
      maxWidth: "50rem",
      marginTop: isXs ? "0" : "0%",
      marginBottom: isXs ? "0" : "0%",
      maxHeight: "90vh",
      overflowY: "auto",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
      WebkitOverflowScrolling: "touch",
    },

    // ── Form primitives ──────────────────────────────────────
    formGroup: {
      marginBottom: spacing.margin,
      display: "flex",
      flexDirection: "column",
      gap: "0.375rem",
    },

    formRow: {
      display: "grid",
      gridTemplateColumns: isXs ? "1fr" : "1fr 1fr",
      gap: spacing.gap,
    },

    sectionTitle: {
      color: isDark
        ? "rgba(255,255,255,0.9)"
        : "rgba(26,26,26,0.8)",
      fontSize: fonts.subtitle,
      fontWeight: "600",
      margin: `${spacing.margin} 0 ${isXs ? "0.75rem" : "1rem"} 0`,
      paddingBottom: "0.5rem",
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(0,0,0,0.1)",
    },

    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: isXs ? "1.5rem" : "2rem",
      borderBottom: isDark
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(0,0,0,0.1)",
      paddingBottom: isXs ? "0.75rem" : "1rem",
      flexWrap: isXs ? "wrap" : "nowrap",
      gap: isXs ? "1rem" : "0",
    },

    modalTitle: {
      color: isDark ? "white" : "#1a1a1a",
      fontSize: isXs ? "1.25rem" : "1.5rem",
      fontWeight: "700",
      margin: 0,
    },

    modalActions: {
      display: "flex",
      gap: isXs ? "0.75rem" : "0.75rem",
      justifyContent: isXs ? "center" : "flex-end",
      marginTop: isXs ? "1.5rem" : "2rem",
      paddingTop: spacing.padding,
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.1)"
        : "1px solid rgba(0,0,0,0.1)",
      flexWrap: isXs ? "wrap" : "nowrap",
    },

    closeBtn: {
      background: "none",
      border: "none",
      color: isDark ? "white" : "#1a1a1a",
      cursor: "pointer",
      padding: "0.5rem",
      borderRadius: "0.5rem",
      transition: "background 0.2s",
    },

    formLabel: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      color: isDark
        ? "rgba(241,245,249,0.9)"
        : "rgba(15,23,42,0.8)",
      fontSize: fonts.body,
      fontWeight: "600",
      marginBottom: "0.625rem",
      letterSpacing: "0.01em",
    },

    formInput: {
      background: isDark ? "rgba(30,41,59,0.8)" : "rgba(248,250,252,0.9)",
      border: isDark
        ? "2px solid rgba(148,163,184,0.2)"
        : "2px solid rgba(148,163,184,0.3)",
      borderRadius: "0.75rem",
      padding: isXs ? "0.875rem 1rem" : "1rem 1.25rem",
      color: isDark ? "#f1f5f9" : "#0f172a",
      fontSize: fonts.body,
      fontWeight: "500",
      outline: "none",
      width: "100%",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxSizing: "border-box",
      boxShadow: isDark
        ? "inset 0 2px 4px rgba(0,0,0,0.2)"
        : "inset 0 1px 2px rgba(0,0,0,0.05)",
    },

    formTextarea: {
      background: isDark ? "rgba(30,41,59,0.8)" : "rgba(248,250,252,0.9)",
      border: isDark
        ? "2px solid rgba(148,163,184,0.2)"
        : "2px solid rgba(148,163,184,0.3)",
      borderRadius: "0.75rem",
      padding: isXs ? "0.875rem 1rem" : "1rem 1.25rem",
      color: isDark ? "#f1f5f9" : "#0f172a",
      fontSize: fonts.body,
      fontWeight: "500",
      outline: "none",
      width: "100%",
      minHeight: "150px",
      resize: "vertical",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      fontFamily: "inherit",
      boxSizing: "border-box",
      boxShadow: isDark
        ? "inset 0 2px 4px rgba(0,0,0,0.2)"
        : "inset 0 1px 2px rgba(0,0,0,0.05)",
    },

    formSelect: {
      appearance: "none",
      background: isDark ? "rgba(30,41,59,0.8)" : "rgba(248,250,252,0.9)",
      border: isDark
        ? "2px solid rgba(148,163,184,0.2)"
        : "2px solid rgba(148,163,184,0.3)",
      borderRadius: "0.75rem",
      padding: isXs ? "0.875rem 1rem" : "1rem 1.25rem",
      color: isDark ? "#f1f5f9" : "#0f172a",
      fontSize: fonts.body,
      fontWeight: "500",
      outline: "none",
      width: "100%",
      cursor: "pointer",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxSizing: "border-box",
      boxShadow: isDark
        ? "inset 0 2px 4px rgba(0,0,0,0.2)"
        : "inset 0 1px 2px rgba(0,0,0,0.05)",
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${isDark ? "%23d1d5db" : "%23374151"
        }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: isRtl ? "left 0.75rem center" : "right 0.75rem center",
      paddingRight: isRtl ? "2.5rem" : undefined,
      paddingLeft: isRtl ? "1rem" : "2.5rem",
    },

    errorText: {
      color: "#ef4444",
      fontSize: fonts.small,
      marginTop: "0.25rem",
      display: "block",
    },

    uploadArea: {
      border: isDark
        ? "2px dashed rgba(255,255,255,0.2)"
        : "2px dashed rgba(0,0,0,0.2)",
      borderRadius: "0.75rem",
      padding: isXs ? "1.5rem" : "2rem",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
    },

    attachmentItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0.75rem",
      background: isDark
        ? "rgba(59,130,246,0.1)"
        : "rgba(59,130,246,0.08)",
      border: "1px solid rgba(59,130,246,0.2)",
      borderRadius: "0.5rem",
      marginTop: "0.5rem",
    },

    submitBtn: {
      background: "#2e7cba",
      color: "white",
      border: "none",
      padding: isXs ? "0.875rem 1.5rem" : "1rem 2rem",
      borderRadius: "0.75rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: "0 0.25rem 0.75rem rgba(46,124,186,0.3)",
      width: "100%",
    },

    cancelBtn: {
      background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
      color: isDark ? "white" : "#1a1a1a",
      border: isDark
        ? "2px solid rgba(255,255,255,0.2)"
        : "2px solid rgba(0,0,0,0.1)",
      padding: isXs ? "0.875rem 1.5rem" : "1rem 2rem",
      borderRadius: "0.75rem",
      cursor: "pointer",
      fontSize: fonts.body,
      fontWeight: "600",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      width: "100%",
    },

    emptyState: {
      textAlign: "center",
      padding: isXs ? "3rem 1rem" : "4rem 2rem",
      color: isDark
        ? "rgba(255,255,255,0.5)"
        : "rgba(26,26,26,0.5)",
    },

    // ── Inline filter select (reused in both tabs) ───────────
    inlineSelect: (isDark, language, fonts, screenSize) => ({
      appearance: "none",
      background: isDark
        ? "rgba(30,41,59,0.8)"
        : "rgba(255,255,255,0.95)",
      border: isDark
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(148,163,184,0.2)",
      borderRadius: "0.75rem",
      padding: screenSize === "xs"
        ? "0.75rem"
        : "0.625rem 2rem 0.625rem 0.875rem",
      color: isDark
        ? "rgba(248,250,252,0.95)"
        : "rgba(30,41,59,0.9)",
      fontSize: fonts.body,
      outline: "none",
      width: "100%",
      cursor: "pointer",
      transition: "all 0.3s ease",
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='${isDark ? "%23d1d5db" : "%23374151"
        }' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`,
      backgroundRepeat: "no-repeat",
      backgroundPosition: language === "ar"
        ? "left 0.75rem center"
        : "right 0.75rem center",
    }),
  };
};

export default useStyles;